import { User } from 'lucide-react';
import type { GuestProfileResponse } from '@/shared/api';
import { PanelCard, FieldGrid, Field } from './PanelCard';
import { ProvenanceTag } from './ProvenanceTag';

function addressLine(g: GuestProfileResponse): string | null {
  const parts = [
    g.streetAddress,
    [g.postalCode, g.city].filter(Boolean).join(' ') || null,
    g.country,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
}

// Gast-Karte. PII (vom Gast im Onboarding, AES-verschluesselt) — nur Admin/Staff. Ohne Profil
// (Onboarding nicht gestartet) zeigen wir den OTA-Namen + Hinweis, ohne PII zu fetchen.
export function GuestContextCard({
  guest,
  fallbackName,
  state = 'ready',
}: {
  guest: GuestProfileResponse | null;
  fallbackName: string | null;
  /** Abruf läuft oder scheiterte — dann ist „noch kein Onboarding“ nicht die Wahrheit. */
  state?: 'ready' | 'loading' | 'error';
}) {
  if (!guest && state !== 'ready') {
    return (
      <PanelCard
        icon={User}
        title="Gast"
        hint={state === 'error' ? 'Gastdaten nicht geladen' : 'Gastdaten werden geladen'}
      >
        <p
          className={state === 'error' ? 'text-sealwax text-[13px]' : 'text-ink-faint text-[13px]'}
        >
          {state === 'error'
            ? 'Die Gastdaten konnten nicht geladen werden.'
            : 'Gastdaten werden geladen …'}
        </p>
      </PanelCard>
    );
  }
  if (!guest) {
    return (
      <PanelCard
        icon={User}
        title="Gast"
        hint="Kontaktdaten erst nach dem Gast-Onboarding"
        trailing={<ProvenanceTag source="ota" />}
      >
        <p className="text-ink text-[13px] font-medium">{fallbackName ?? '—'}</p>
        <p className="text-ink-faint mt-1 text-[12px]">
          Kontaktdaten erst nach dem Gast-Onboarding verfügbar.
        </p>
      </PanelCard>
    );
  }

  return (
    <PanelCard
      icon={User}
      title="Gast"
      hint="Name, Telefon, E-Mail"
      trailing={<ProvenanceTag source="guest" />}
    >
      <p className="text-ink font-serif text-lg leading-tight">
        {guest.firstName} {guest.lastName}
      </p>
      <div className="mt-3">
        <FieldGrid>
          <Field
            wide
            label="Telefon"
            mono
            value={
              guest.phone ? (
                <a href={`tel:${guest.phone}`} className="text-forest underline underline-offset-2">
                  {guest.phone}
                </a>
              ) : null
            }
          />
          <Field
            wide
            label="E-Mail"
            value={
              guest.email ? (
                <a
                  href={`mailto:${guest.email}`}
                  className="text-forest underline underline-offset-2"
                >
                  {guest.email}
                </a>
              ) : null
            }
          />
          <Field wide label="Nationalität" value={guest.nationality} />
          <Field wide label="Adresse" value={addressLine(guest)} />
        </FieldGrid>
      </div>
    </PanelCard>
  );
}
